import type { EscrowStatus } from "@remi/enums/escrow-status";
import type { FileTag } from "@remi/enums/file-tag";
import type { FinancingVendor } from "@remi/enums/financing-vendor";
import type { SowChangeType } from "@remi/enums/sow-change-type";

import { DbErrorType, type QueryTimeoutCode } from "./db-codes";

export { DbErrorType, QueryTimeoutCode } from "./db-codes";

type ErrorDetails = {
  sentryDescription: string;
};

export type TaskFkField = "projectId" | "teamId" | "assigneeId";

export type DbErrors =
  | {
      errorType: typeof DbErrorType.INVALID_ADDRESS_COORDINATES;
      data: {
        addressCoordinates: unknown;
      };
    }
  | {
      errorType: typeof DbErrorType.ACTIONABILITY_FILTER_WITHOUT_MILESTONE_CONTEXT;
      data: { columns: string };
    }
  | {
      errorType: typeof DbErrorType.PHONE_PROXY_LOCK_OUTSIDE_TRANSACTION;
      data: { phoneId: string };
    }
  | {
      errorType: typeof DbErrorType.TASK_EVENT_LOCK_OUTSIDE_TRANSACTION;
      data: { taskId: string };
    }
  | {
      errorType: typeof DbErrorType.PRISMA_CLIENT_RUST_PANIC;
      data: {
        args: unknown[];
      };
    }
  | {
      errorType: typeof DbErrorType.PRISMA_CLIENT_KNOWN_REQUEST_ERROR;
      data: {
        args: unknown[];
      };
    }
  | {
      errorType: typeof DbErrorType.INVALID_ENUM_VALUE;
      data: { enum: string; value: string };
    }
  | {
      errorType: typeof DbErrorType.APPEND_ONLY_TABLE_MUTATION;
      data: { model: string; operation: string };
    }
  | {
      errorType: typeof DbErrorType.BID_REQUEST_SUNRUN_RECORD_ID_CONFLICT;
      data: {
        sunrunRecordId: string;
        externalId: string;
        existingBidRequestId: string;
        existingExternalId: string | null;
      };
    }
  | {
      errorType: typeof DbErrorType.PROXY_PHONE_NUMBER_USED_AS_CONTACT;
      data: {
        phoneNumber: string;
      };
    }
  | {
      errorType: typeof DbErrorType.SYSTEM_PHONE_NUMBER_USED_AS_CONTACT;
      data: {
        phoneNumber: string;
      };
    }
  | {
      errorType: typeof DbErrorType.ACTIVE_QUOTE_AND_ACTIVE_QUOTE_VERSION;
      data: {
        rfqId: number;
      };
    }
  | {
      errorType: typeof DbErrorType.ESTIMATE_CONVERSION_VERSION_MISSING_MAPPINGS;
      data: {
        quoteVersionId: string;
        quoteId: string;
        requestForQuoteId: number;
        quotingSource: string;
        lineItemCount: number;
      };
    }
  | {
      errorType: typeof DbErrorType.FILE_TAG_APPROVAL_READBACK_MISSED;
      data: {
        fileId: string;
        tagId: FileTag;
      };
    }
  | {
      errorType: typeof DbErrorType.REQUEST_FOR_QUOTE_NOT_FOUND;
      data: { requestForQuoteId: number };
    }
  | {
      errorType: typeof DbErrorType.CHANGE_ORDER_NOT_FOUND;
      data: { changeOrderId: string };
    }
  | {
      errorType: typeof DbErrorType.CHANGE_ORDER_LINE_ITEM_NOT_FOUND;
      data: { changeOrderLineItemId: string };
    }
  | {
      errorType: typeof DbErrorType.ESCROW_NOT_ON_REQUEST_FOR_QUOTE;
      data: { escrowId: string; requestForQuoteId: number };
    }
  | {
      errorType: typeof DbErrorType.FINANCING_NOT_ON_REQUEST_FOR_QUOTE;
      data: { financingId: string; requestForQuoteId: number };
    }
  | {
      errorType: typeof DbErrorType.QUOTE_VERSION_NOT_ON_REQUEST_FOR_QUOTE;
      data: { quoteVersionId: string; requestForQuoteId: number };
    }
  | {
      errorType: typeof DbErrorType.QUOTE_VERSION_ARCHIVED;
      data: { quoteVersionId: string; requestForQuoteId: number };
    }
  | {
      errorType: typeof DbErrorType.VENDOR_CREDIT_NOT_FOUND;
      data: { vendorCreditId: string };
    }
  | {
      errorType: typeof DbErrorType.CREDIT_MEMO_NOT_FOUND;
      data: { creditMemoId: string };
    }
  | {
      errorType: typeof DbErrorType.RECRUITING_ROOFER_NOT_FOUND;
      data: { rooferId: string };
    }
  | {
      errorType: typeof DbErrorType.RECRUITING_MARKET_NOT_FOUND;
      data: { marketId: string };
    }
  | {
      errorType: typeof DbErrorType.RECRUITING_ACTIVITY_NOT_FOUND;
      data: { activityId: string };
    }
  | {
      errorType: typeof DbErrorType.RECRUITING_ACTIVITY_TRIAGE_NOT_FOUND;
      data: { triageId: string };
    }
  | {
      errorType: typeof DbErrorType.RECRUITING_IMPORT_ROW_NOT_FOUND;
      data: { kind: "ROOFER" | "ACTIVITY"; reference: string };
    }
  | {
      errorType: typeof DbErrorType.ROUTING_TERRITORY_NOT_FOUND;
      data: { routingTerritoryId: string };
    }
  | {
      errorType: typeof DbErrorType.REIMBURSEMENT_NOT_FOUND;
      data: { reimbursementId: string };
    }
  | {
      errorType: typeof DbErrorType.INVOICE_NOT_FOUND;
      data: { invoiceId: string };
    }
  | {
      errorType: typeof DbErrorType.BILL_NOT_FOUND;
      data: { billId: string };
    }
  | {
      errorType: typeof DbErrorType.WORK_ORDER_NOT_FOUND;
      data: { workOrderId: number };
    }
  | {
      errorType: typeof DbErrorType.TASK_NOT_FOUND;
      data: { taskId: string } | { projectIdTypePairs: string };
    }
  | {
      errorType: typeof DbErrorType.TASK_CREATE_FK_VIOLATION;
      data: { violatedField: TaskFkField; projectIdTypePairs: string };
    }
  | {
      errorType: typeof DbErrorType.TASK_CREATE_INVARIANT_VIOLATION;
      data: { projectIdTypePairs: string };
    }
  | {
      errorType: typeof DbErrorType.HOLDUP_NOT_FOUND;
      data: { holdupId: string };
    }
  | {
      errorType: typeof DbErrorType.TASK_EVENT_INVARIANT_VIOLATION;
      data: { taskId: string; corruptedField: string; expectedType: string; actualValue: unknown };
    }
  | {
      errorType: typeof DbErrorType.TASK_EVENT_REASON_NOT_RETIRED;
      data: { taskEventId: string; from: string };
    }
  | {
      errorType: typeof DbErrorType.DUPLICATE_ESTIMATE_COVERAGE;
      data: { estimateId: string; coverageType: string };
    }
  | {
      errorType: typeof DbErrorType.ESTIMATE_COVERAGE_NOT_FOUND;
      data: { estimateCoverageId: string };
    }
  | {
      errorType: typeof DbErrorType.ESTIMATE_QA_RULE_UNREADABLE;
      data: { estimateQaRuleId: string };
    }
  | {
      errorType: typeof DbErrorType.SLA_MATCHER_UNREADABLE;
      data: { slaMatcherId: string; slaDefinitionId: string };
    }
  | {
      errorType: typeof DbErrorType.DISPATCH_BUCKET_ROWS_UNREADABLE;
      data: { organizationId: string; limit: number; issue: string };
    }
  | {
      errorType: typeof DbErrorType.ESTIMATE_QA_RULE_NOT_FOUND;
      data: { estimateQaRuleId: string };
    }
  | {
      errorType: typeof DbErrorType.AGENT_ACTION_REQUEST_NOT_TASK_TARGETED;
      data: { agentActionRequestId: string };
    }
  | {
      errorType: typeof DbErrorType.SLA_DEFINITION_NOT_FOUND;
      data: { slaDefinitionId: string };
    }
  | {
      errorType: typeof DbErrorType.LOAN_PLAN_NOT_FOUND;
      data: { vendor: string; loanPlanId: string };
    }
  | {
      errorType: typeof DbErrorType.SLA_DEFINITION_UNREADABLE;
      data: { slaDefinitionId: string; invalidFields: string[] };
    }
  | {
      errorType: typeof DbErrorType.PERSON_FAVORITE_NOT_FOUND;
      data: { personFavoriteId: string };
    }
  | {
      errorType: typeof DbErrorType.WEB_PUSH_SUBSCRIPTION_NOT_FOUND;
      data: { webPushSubscriptionId: string };
    }
  | {
      errorType: typeof DbErrorType.CALENDAR_APPOINTMENT_NOT_FOUND;
      data: { calendarAppointmentId: string };
    }
  | {
      errorType: typeof DbErrorType.TRAINING_MODULE_NOT_FOUND;
      data: { trainingModuleId: string };
    }
  | {
      errorType: typeof DbErrorType.CONTRACTOR_TRAINING_SESSION_NOT_FOUND;
      data: { contractorTrainingSessionId: string };
    }
  | {
      errorType: typeof DbErrorType.PERSON_SIDEBAR_ROW_NOT_FOUND;
      data: { personSidebarRowId: string };
    }
  | {
      errorType: typeof DbErrorType.TICKET_NOT_FOUND;
      data: { ticketId: string };
    }
  | {
      errorType: typeof DbErrorType.QUERY_TIMEOUT;
      data: {
        query: string;
        searchLength: number;
        hasRegionFilter: boolean;
        phase?: string;
        timeoutCode: QueryTimeoutCode;
      };
    }
  | {
      errorType: typeof DbErrorType.SERVICE_AREA_NOT_FOUND;
      data: { serviceAreaId: string };
    }
  | {
      errorType: typeof DbErrorType.QA_COVERED_PROJECT_NOT_FOUND;
      data: { qaProjectId: number; coveredProjectId: number };
    }
  | {
      errorType: typeof DbErrorType.PROJECT_OWNER_REMOVE_FILTER_TOO_BROAD;
      data: { where: unknown };
    }
  | {
      errorType: typeof DbErrorType.PROJECT_OWNER_BATCH_UPDATE_MATCHED_NOTHING;
      data: { projectIds: number[]; roles: string[]; expected: number; matched: number };
    }
  | {
      errorType: typeof DbErrorType.PROPERTY_ADDRESS_NOT_FOUND;
      data: { propertyAddressId: string };
    }
  | {
      errorType: typeof DbErrorType.HOME_BASE_EDIT_OF_ADDRESS_PAST_PRE_PRODUCTION;
      data: { propertyAddressId: string; workSiteCount: number };
    }
  | {
      errorType: typeof DbErrorType.CONTRACTOR_PROFILE_NOT_FOUND;
      data: { organizationId: string };
    }
  | {
      errorType: typeof DbErrorType.KEY_VALUE_NULLISH_VALUE;
      data: { key: string };
    }
  | {
      errorType: typeof DbErrorType.UNAUTHORIZED_GROUP_COLUMN;
      data: { columnId: string };
    }
  | {
      errorType: typeof DbErrorType.INVALID_SQL_IDENTIFIER;
      data: { identifier: string };
    }
  | {
      errorType: typeof DbErrorType.MERGE_MANIFEST_PHONE_MISMATCH;
      data: { priorPhoneId: string; currentPhoneId: string };
    }
  | {
      errorType: typeof DbErrorType.PERSON_MERGE_UNMOVABLE_PROJECT_ROLE;
      data: { role: string; personId: string };
    }
  | {
      errorType: typeof DbErrorType.RAW_QUERY_ROW_SHAPE_MISMATCH;
      data: { query: string };
    }
  | {
      errorType: typeof DbErrorType.MISSING_DATE_BUCKET_BOUNDARIES;
      data: { columnId: string };
    }
  | {
      errorType: typeof DbErrorType.SPATIAL_OVERLAY_FAILURE;
      data: { method: string; errno: string };
    }
  | {
      errorType: typeof DbErrorType.PROXY_PHONE_NOT_FOUND;
      data: { proxyPhoneId: string };
    }
  | {
      errorType: typeof DbErrorType.PROXY_PHONE_ASSIGNMENT_NOT_FOUND;
      data: { proxyPhoneId: string; contractorOrgId: string };
    }
  | {
      errorType: typeof DbErrorType.PROXY_PHONE_IS_DEDICATED;
      data: { proxyPhoneId: string; boundPhoneId: string };
    }
  | {
      errorType: typeof DbErrorType.HOMEOWNER_PHONE_HAS_DEDICATED_PROXY;
      data: { homeownerPhoneId: string; dedicatedProxyPhoneId: string };
    }
  | {
      errorType: typeof DbErrorType.PHONE_NOT_FOUND;
      data: { phoneId: string };
    }
  | {
      errorType: typeof DbErrorType.PHONE_OWNER_STILL_ENABLED;
      data: { personIds: string[] };
    }
  | {
      errorType: typeof DbErrorType.PHONE_OWNER_REJECTED_BY_DETACH_GUARD;
      data: { phoneIds: string[] };
    }
  | {
      errorType: typeof DbErrorType.PHONE_DETACH_BATCH_TOO_LARGE;
      data: { count: number; max: number };
    }
  | {
      errorType: typeof DbErrorType.PHONE_ADOPTION_RACED;
      data: { phoneId: string };
    }
  | {
      errorType: typeof DbErrorType.WORK_SITE_NOT_FOUND;
      data: { workSiteId: number };
    }
  | {
      errorType: typeof DbErrorType.WORK_SITE_ROLE_ASSIGNMENT_NOT_FOUND;
      data: { workSiteId: number; role: string };
    }
  | {
      errorType: typeof DbErrorType.PERSON_TO_SERVICE_AREA_NOT_FOUND;
      data: { serviceAreaId: string; role: string };
    }
  | {
      errorType: typeof DbErrorType.TICKET_TO_TAG_NOT_FOUND;
      data: { ticketId: string; tagId: string };
    }
  | {
      errorType: typeof DbErrorType.PROPERTY_MODEL_CONVERSION_FAILED;
      data: { source: string; reason: string };
    }
  | {
      errorType: typeof DbErrorType.PROPERTY_MODEL_NOT_FOUND;
      data: { propertyModelId: string };
    }
  | {
      errorType: typeof DbErrorType.PROPERTY_MODEL_ELEMENT_NOT_FOUND;
      data: { elementClass: string; elementId: string };
    }
  | {
      errorType: typeof DbErrorType.BID_NOT_FOUND;
      data: { bidId: number };
    }
  | {
      errorType: typeof DbErrorType.BID_DATA_NOT_FOUND;
      data: { bidDataId: string };
    }
  | {
      errorType: typeof DbErrorType.PERSON_NOT_FOUND;
      data: { personId: string };
    }
  | {
      errorType: typeof DbErrorType.CONVERSATION_BID_LINK_WRITE_RACED;
      data: { intendedCount: number; insertedCount: number; expectedInsertedCount: number };
    }
  | {
      errorType: typeof DbErrorType.MARKETING_LEAD_CREATE_RACED;
      data: { leadEventId: string };
    }
  | {
      errorType: typeof DbErrorType.MARKETING_LEAD_NOT_FOUND;
      data: { marketingLeadId: string };
    }
  | {
      errorType: typeof DbErrorType.MARKETING_LEAD_STAGE_CREATE_RACED;
      data: { marketingLeadId: string; leadStage: string };
    }
  | {
      errorType: typeof DbErrorType.CONVERSATION_NOT_FOUND;
      data: { conversationId: string };
    }
  | {
      errorType: typeof DbErrorType.BID_REQUEST_EXTERNAL_ID_CLAIMED;
      data: { externalId: string; organizationId: string };
    }
  | {
      errorType: typeof DbErrorType.FINANCING_CANCEL_BLOCKED_DISBURSED;
      data: { financingId: string; externalId: string; vendor: FinancingVendor; disbursedAmount: number };
    }
  | {
      errorType: typeof DbErrorType.FINANCING_NOT_FOUND;
      data: { externalId: string; vendor: FinancingVendor };
    }
  | {
      errorType: typeof DbErrorType.FINANCING_WEBHOOK_EVENT_INSERT_FAILED;
      data: { vendor: FinancingVendor; deliveryKey: string | null; prismaErrorCode: string | null };
    }
  | {
      errorType: typeof DbErrorType.ESCROW_TERMINAL_STATUS;
      data: { escrowId: string; requestForQuoteId: number; status: EscrowStatus };
    }
  | {
      errorType: typeof DbErrorType.BLANK_EMAIL_ADDRESS;
      data: { personId: string };
    }
  | {
      errorType: typeof DbErrorType.EMAIL_ADDRESS_NOT_FOUND;
      data: { emailAddressId: string };
    }
  | {
      errorType: typeof DbErrorType.TELECOM_PROVIDER_USER_NOT_FOUND;
      data: { telecomProviderUserId: string };
    }
  | {
      errorType: typeof DbErrorType.PERSON_PRIMARY_EMAIL_WRITE_RACED;
      data: { personId: string; expectedPrimaryEmailAddressId: string };
    }
  | {
      errorType: typeof DbErrorType.CONTRACTOR_CAPABILITY_GROUPING_NOT_FOUND;
      data: { contractorCapabilityGroupingId: string };
    }
  | {
      errorType: typeof DbErrorType.CONTRACTOR_CAPABILITY_GROUPING_HAS_LINE_ITEMS;
      data: { contractorCapabilityGroupingId: string; lineItemCount: number };
    }
  | {
      errorType: typeof DbErrorType.TELECOM_PROVIDER_PHONE_NOT_FOUND;
      data: { telecomProviderPhoneId: string };
    }
  | {
      errorType: typeof DbErrorType.WORK_ORDER_CONCESSION_SAME_STATUS_TRANSITION;
      data: { concessionId: string; status: string };
    }
  | {
      errorType: typeof DbErrorType.WORK_ORDER_CONCESSION_ALREADY_OPEN;
      data: { hostTaskId: string; openConcessionId: string };
    }
  | {
      errorType: typeof DbErrorType.EMAIL_PROVIDER_MAILBOX_NOT_FOUND;
      data: { emailProviderMailboxId: string };
    }
  | {
      errorType: typeof DbErrorType.SOW_CHANGE_PAYLOAD_INVALID;
      data: { sowChangeId: string; type: SowChangeType; payloadVersion: number };
    }
  | {
      errorType: typeof DbErrorType.SOW_CHANGE_LOCK_OUTSIDE_TRANSACTION;
      data: { sowChangeId: string };
    };

export const dbErrorDetails: Record<DbErrorType, ErrorDetails> = {
  [DbErrorType.INVALID_ADDRESS_COORDINATES]: {
    sentryDescription: "Invalid address coordinates provided to database operation",
  },
  [DbErrorType.ACTIONABILITY_FILTER_WITHOUT_MILESTONE_CONTEXT]: {
    sentryDescription: "isNotActionable filter requires milestone context (milestone column or filter)",
  },
  [DbErrorType.INVALID_ENUM_VALUE]: {
    sentryDescription: "Invalid enum value",
  },
  [DbErrorType.APPEND_ONLY_TABLE_MUTATION]: {
    sentryDescription: "Mutation attempted on an append-only table",
  },
  [DbErrorType.PRISMA_CLIENT_RUST_PANIC]: {
    sentryDescription: "Prisma Client Rust panic error occurred",
  },
  [DbErrorType.PRISMA_CLIENT_KNOWN_REQUEST_ERROR]: {
    sentryDescription: "Prisma Client known request error occurred",
  },
  [DbErrorType.BID_REQUEST_SUNRUN_RECORD_ID_CONFLICT]: {
    sentryDescription:
      "Bid request upsert: sunrunRecordId already assigned (concurrent ingest); continuing without sunrunRecordId on this upsert",
  },
  [DbErrorType.PROXY_PHONE_NUMBER_USED_AS_CONTACT]: {
    sentryDescription: "Proxy phone number cannot be used as a contact phone number",
  },
  [DbErrorType.SYSTEM_PHONE_NUMBER_USED_AS_CONTACT]: {
    sentryDescription: "System phone number cannot be used as a contact phone number",
  },
  [DbErrorType.ACTIVE_QUOTE_AND_ACTIVE_QUOTE_VERSION]: {
    sentryDescription: "RFQ has both activeQuote and activeQuoteVersion — expected only one",
  },
  [DbErrorType.ESTIMATE_CONVERSION_VERSION_MISSING_MAPPINGS]: {
    sentryDescription:
      "ESTIMATE_CONVERSION quote version written with line items but no estimate-line-item mappings — its quote card will render $0",
  },
  [DbErrorType.FILE_TAG_APPROVAL_READBACK_MISSED]: {
    sentryDescription: "File tag approval readback found no row — concurrent untag won the race",
  },
  [DbErrorType.REQUEST_FOR_QUOTE_NOT_FOUND]: {
    sentryDescription: "Request for quote not found for update",
  },
  [DbErrorType.CHANGE_ORDER_NOT_FOUND]: {
    sentryDescription: "Change order not found for update",
  },
  [DbErrorType.CHANGE_ORDER_LINE_ITEM_NOT_FOUND]: {
    sentryDescription: "Change order line item not found for update",
  },
  [DbErrorType.ESCROW_NOT_ON_REQUEST_FOR_QUOTE]: {
    sentryDescription: "Escrow does not belong to the request for quote it was activated on",
  },
  [DbErrorType.FINANCING_NOT_ON_REQUEST_FOR_QUOTE]: {
    sentryDescription: "Financing does not belong to the request for quote it was activated on",
  },
  [DbErrorType.QUOTE_VERSION_NOT_ON_REQUEST_FOR_QUOTE]: {
    sentryDescription: "Quote version does not belong to the request for quote it was activated on",
  },
  [DbErrorType.QUOTE_VERSION_ARCHIVED]: {
    sentryDescription: "Quote version is archived and cannot be staged to activate on funding",
  },
  [DbErrorType.VENDOR_CREDIT_NOT_FOUND]: {
    sentryDescription: "Vendor credit not found for update",
  },
  [DbErrorType.CREDIT_MEMO_NOT_FOUND]: {
    sentryDescription: "Credit memo not found for update",
  },
  [DbErrorType.RECRUITING_ROOFER_NOT_FOUND]: {
    sentryDescription: "Recruiting roofer not found for update",
  },
  [DbErrorType.RECRUITING_MARKET_NOT_FOUND]: {
    sentryDescription: "Recruiting market not found for update",
  },
  [DbErrorType.RECRUITING_ACTIVITY_NOT_FOUND]: {
    sentryDescription: "Recruiting activity not found for update",
  },
  [DbErrorType.RECRUITING_ACTIVITY_TRIAGE_NOT_FOUND]: {
    sentryDescription: "Recruiting activity triage not found for update",
  },
  [DbErrorType.RECRUITING_IMPORT_ROW_NOT_FOUND]: {
    sentryDescription: "An Airtable import row was not found after its insert in the same transaction",
  },
  [DbErrorType.ROUTING_TERRITORY_NOT_FOUND]: {
    sentryDescription: "Routing territory not found for update",
  },
  [DbErrorType.REIMBURSEMENT_NOT_FOUND]: {
    sentryDescription: "Reimbursement not found for update",
  },
  [DbErrorType.INVOICE_NOT_FOUND]: {
    sentryDescription: "Invoice not found for update",
  },
  [DbErrorType.BILL_NOT_FOUND]: {
    sentryDescription: "Bill not found for update",
  },
  [DbErrorType.WORK_ORDER_NOT_FOUND]: {
    sentryDescription: "Work order not found for update",
  },
  [DbErrorType.TASK_NOT_FOUND]: {
    sentryDescription: "Task not found for update",
  },
  [DbErrorType.TASK_CREATE_FK_VIOLATION]: {
    sentryDescription: "Task create referenced a parent row that does not exist",
  },
  [DbErrorType.TASK_CREATE_INVARIANT_VIOLATION]: {
    sentryDescription:
      "Task create reached a state its insert partition makes impossible — a code fault in TaskDao, not a missing parent row",
  },
  [DbErrorType.HOLDUP_NOT_FOUND]: {
    sentryDescription: "Holdup not found for release",
  },
  [DbErrorType.TASK_EVENT_INVARIANT_VIOLATION]: {
    sentryDescription:
      "A TaskEvent column the reading code requires was null — the row does not satisfy the shape its event type promises",
  },
  [DbErrorType.TASK_EVENT_REASON_NOT_RETIRED]: {
    sentryDescription: "A TaskEvent reason rewrite named a reason that ATTEMPT_REASON_REPLACEMENTS does not retire",
  },
  [DbErrorType.TICKET_NOT_FOUND]: {
    sentryDescription: "Ticket not found for update",
  },
  [DbErrorType.QUERY_TIMEOUT]: {
    sentryDescription: "Database query exceeded execution time limit",
  },
  [DbErrorType.SERVICE_AREA_NOT_FOUND]: {
    sentryDescription: "Service area not found for update",
  },
  [DbErrorType.QA_COVERED_PROJECT_NOT_FOUND]: {
    sentryDescription: "QA coverage row not found for the covered project being updated",
  },
  [DbErrorType.PROJECT_OWNER_REMOVE_FILTER_TOO_BROAD]: {
    sentryDescription:
      "removeOwners called with only projectId — would soft-delete every role assignment on the project",
  },
  [DbErrorType.PROJECT_OWNER_BATCH_UPDATE_MATCHED_NOTHING]: {
    sentryDescription: "PersonToProject batch update matched no rows despite reading them live in the same transaction",
  },
  [DbErrorType.PROPERTY_ADDRESS_NOT_FOUND]: {
    sentryDescription: "Property address not found for update",
  },
  [DbErrorType.HOME_BASE_EDIT_OF_ADDRESS_PAST_PRE_PRODUCTION]: {
    sentryDescription: "A home-base edit would have changed an address a worksite past pre-production uses",
  },
  [DbErrorType.CONTRACTOR_PROFILE_NOT_FOUND]: {
    sentryDescription: "Contractor profile not found for update",
  },
  [DbErrorType.KEY_VALUE_NULLISH_VALUE]: {
    sentryDescription: "Key-value cache write rejected a null or undefined value",
  },
  [DbErrorType.UNAUTHORIZED_GROUP_COLUMN]: {
    sentryDescription: "Group column requested by a user lacking the column's read capability",
  },
  [DbErrorType.INVALID_SQL_IDENTIFIER]: {
    sentryDescription: "A model/column identifier failed the SQL-identifier allowlist regex before interpolation",
  },
  [DbErrorType.MERGE_MANIFEST_PHONE_MISMATCH]: {
    sentryDescription: "Merge-manifest undo would re-point SMS to a phone with a different number or extension",
  },
  [DbErrorType.PERSON_MERGE_UNMOVABLE_PROJECT_ROLE]: {
    sentryDescription: "A person merge would evict a project owner whose role addOwners cannot re-create",
  },
  [DbErrorType.RAW_QUERY_ROW_SHAPE_MISMATCH]: {
    sentryDescription: "A raw SQL result did not match the row schema it is parsed with",
  },
  [DbErrorType.MISSING_DATE_BUCKET_BOUNDARIES]: {
    sentryDescription: "Date column grouping reached the DAO without the required dateBucketBoundaries",
  },
  [DbErrorType.SPATIAL_OVERLAY_FAILURE]: {
    sentryDescription: "Boost.Geometry overlay failed after retry — non-deterministic Vitess replica routing",
  },
  [DbErrorType.PROXY_PHONE_NOT_FOUND]: {
    sentryDescription: "Proxy phone not found",
  },
  [DbErrorType.PROXY_PHONE_ASSIGNMENT_NOT_FOUND]: {
    sentryDescription: "Proxy phone assignment not found for expiry",
  },
  [DbErrorType.PROXY_PHONE_IS_DEDICATED]: {
    sentryDescription: "Refused a shared assignment on a proxy phone already bound 1:1",
  },
  [DbErrorType.HOMEOWNER_PHONE_HAS_DEDICATED_PROXY]: {
    sentryDescription: "Refused a shared assignment for a homeowner phone already holding a dedicated proxy",
  },
  [DbErrorType.PHONE_NOT_FOUND]: {
    sentryDescription: "Phone not found for update",
  },
  [DbErrorType.PHONE_OWNER_STILL_ENABLED]: {
    sentryDescription: "Refused to detach a phone whose owner is still staffed",
  },
  [DbErrorType.PHONE_OWNER_REJECTED_BY_DETACH_GUARD]: {
    sentryDescription: "Refused to detach phones whose owners failed the caller's owner guard",
  },
  [DbErrorType.PHONE_DETACH_BATCH_TOO_LARGE]: {
    sentryDescription: "Refused a phone detach batch large enough to lose its all-or-nothing guarantee",
  },
  [DbErrorType.PHONE_ADOPTION_RACED]: {
    sentryDescription: "Refused to mint a placeholder Person onto a Phone tombstoned mid-adoption",
  },
  [DbErrorType.WORK_SITE_NOT_FOUND]: {
    sentryDescription: "Work site not found for update",
  },
  [DbErrorType.WORK_SITE_ROLE_ASSIGNMENT_NOT_FOUND]: {
    sentryDescription: "Work site role assignment not found for update",
  },
  [DbErrorType.PERSON_TO_SERVICE_AREA_NOT_FOUND]: {
    sentryDescription: "Person-to-service-area role assignment not found for update",
  },
  [DbErrorType.TICKET_TO_TAG_NOT_FOUND]: {
    sentryDescription: "Ticket-to-tag membership not found for update",
  },
  [DbErrorType.PROPERTY_MODEL_CONVERSION_FAILED]: {
    sentryDescription: "Failed to convert persisted PropertyModel rows into the domain entity",
  },
  [DbErrorType.PROPERTY_MODEL_NOT_FOUND]: {
    sentryDescription: "No PropertyModel exists for the requested id",
  },
  [DbErrorType.PROPERTY_MODEL_ELEMENT_NOT_FOUND]: {
    sentryDescription: "No PropertyModel face/edge/component exists for the requested element id",
  },
  [DbErrorType.BID_NOT_FOUND]: {
    sentryDescription: "Bid not found for update",
  },
  [DbErrorType.BID_DATA_NOT_FOUND]: {
    sentryDescription: "Bid data not found for update",
  },
  [DbErrorType.PERSON_NOT_FOUND]: {
    sentryDescription: "Person not found for update",
  },
  [DbErrorType.DUPLICATE_ESTIMATE_COVERAGE]: {
    sentryDescription: "An active EstimateCoverage already exists for this estimate and coverage type",
  },
  [DbErrorType.ESTIMATE_COVERAGE_NOT_FOUND]: {
    sentryDescription: "Estimate coverage write targeted a row that is missing or soft-deleted",
  },
  [DbErrorType.ESTIMATE_QA_RULE_UNREADABLE]: {
    sentryDescription: "Estimate QA rule config failed to parse against the current rule schema",
  },
  [DbErrorType.SLA_MATCHER_UNREADABLE]: {
    sentryDescription: "SLA matcher conditions failed to parse; matching without them would match every event",
  },
  [DbErrorType.DISPATCH_BUCKET_ROWS_UNREADABLE]: {
    sentryDescription:
      "Dispatch bucket raw query returned rows that failed to parse; the board must not render them as empty",
  },
  [DbErrorType.ESTIMATE_QA_RULE_NOT_FOUND]: {
    sentryDescription: "Estimate QA rule write targeted a row that is missing or soft-deleted",
  },
  [DbErrorType.AGENT_ACTION_REQUEST_NOT_TASK_TARGETED]: {
    sentryDescription: "Agent action request row has no task target; it predates the target backfill",
  },
  [DbErrorType.SLA_DEFINITION_NOT_FOUND]: {
    sentryDescription: "SLA definition write targeted a row that is missing or soft-deleted",
  },
  [DbErrorType.SLA_DEFINITION_UNREADABLE]: {
    sentryDescription: "SLA definition schedule or matcher conditions failed to parse against the current schema",
  },
  [DbErrorType.PERSON_FAVORITE_NOT_FOUND]: {
    sentryDescription: "Favorite write targeted a row that disappeared between the lookup and the write",
  },
  [DbErrorType.WEB_PUSH_SUBSCRIPTION_NOT_FOUND]: {
    sentryDescription: "Web push subscription write targeted a row that disappeared between the lookup and the write",
  },
  [DbErrorType.CALENDAR_APPOINTMENT_NOT_FOUND]: {
    sentryDescription: "Calendar appointment write targeted a row that disappeared between the lookup and the write",
  },
  [DbErrorType.TRAINING_MODULE_NOT_FOUND]: {
    sentryDescription: "Training module write targeted a row that disappeared between the lookup and the write",
  },
  [DbErrorType.CONTRACTOR_TRAINING_SESSION_NOT_FOUND]: {
    sentryDescription: "Training session write targeted a row that disappeared between the lookup and the write",
  },
  [DbErrorType.PERSON_SIDEBAR_ROW_NOT_FOUND]: {
    sentryDescription: "Sidebar-row write targeted a row that disappeared between the lookup and the write",
  },
  [DbErrorType.CONVERSATION_BID_LINK_WRITE_RACED]: {
    sentryDescription:
      "Conversation bid-link insert raced a concurrent writer, so the confirmed-inserted set is not exact",
  },
  [DbErrorType.MARKETING_LEAD_CREATE_RACED]: {
    sentryDescription: "Marketing lead create collided with an uncommitted row for the same lead event id",
  },
  [DbErrorType.MARKETING_LEAD_NOT_FOUND]: {
    sentryDescription: "Marketing lead was not found",
  },
  [DbErrorType.MARKETING_LEAD_STAGE_CREATE_RACED]: {
    sentryDescription: "Marketing lead stage create collided with an uncommitted row for the same lead and stage",
  },
  [DbErrorType.CONVERSATION_NOT_FOUND]: {
    sentryDescription: "Conversation not found for update",
  },
  [DbErrorType.BID_REQUEST_EXTERNAL_ID_CLAIMED]: {
    sentryDescription: "A concurrent ingest already claimed this external id, so the new bid was rolled back",
  },
  [DbErrorType.FINANCING_CANCEL_BLOCKED_DISBURSED]: {
    sentryDescription: "Financing cancel refused: the loan has money disbursed against it and would have been zeroed",
  },
  [DbErrorType.FINANCING_NOT_FOUND]: {
    sentryDescription: "No financing row exists for the given externalId and vendor",
  },
  [DbErrorType.FINANCING_WEBHOOK_EVENT_INSERT_FAILED]: {
    sentryDescription: "A lender webhook could not be recorded in FinancingWebhookEvent",
  },
  [DbErrorType.ESCROW_TERMINAL_STATUS]: {
    sentryDescription: "Cannot activate an escrow in a terminal status (CANCELLED or DISBURSED)",
  },
  [DbErrorType.BLANK_EMAIL_ADDRESS]: {
    sentryDescription: "Attempted to attach an email address with no address text to a person",
  },
  [DbErrorType.EMAIL_ADDRESS_NOT_FOUND]: {
    sentryDescription: "Email address not found for disconnect",
  },
  [DbErrorType.TELECOM_PROVIDER_USER_NOT_FOUND]: {
    sentryDescription: "Telecom provider user not found for remap",
  },
  [DbErrorType.PERSON_PRIMARY_EMAIL_WRITE_RACED]: {
    sentryDescription:
      "Primary-email pointer write expected an address a concurrent write already moved the pointer off",
  },
  [DbErrorType.CONTRACTOR_CAPABILITY_GROUPING_NOT_FOUND]: {
    sentryDescription: "Contractor capability grouping not found",
  },
  [DbErrorType.CONTRACTOR_CAPABILITY_GROUPING_HAS_LINE_ITEMS]: {
    sentryDescription: "Cannot delete a contractor capability grouping that has assigned line items",
  },
  [DbErrorType.TELECOM_PROVIDER_PHONE_NOT_FOUND]: {
    sentryDescription: "Telecom provider phone not found for a line team update",
  },
  [DbErrorType.EMAIL_PROVIDER_MAILBOX_NOT_FOUND]: {
    sentryDescription: "Email provider mailbox not found for an update or retire",
  },
  [DbErrorType.PHONE_PROXY_LOCK_OUTSIDE_TRANSACTION]: {
    sentryDescription:
      "Phone proxy-binding lock ran on a bare client, where it releases at autocommit and guards nothing",
  },
  [DbErrorType.TASK_EVENT_LOCK_OUTSIDE_TRANSACTION]: {
    sentryDescription: "Task submission lock ran on a bare client, where it releases at autocommit and guards nothing",
  },
  [DbErrorType.WORK_ORDER_CONCESSION_SAME_STATUS_TRANSITION]: {
    sentryDescription: "A work order concession status write named the status the row already has",
  },
  [DbErrorType.WORK_ORDER_CONCESSION_ALREADY_OPEN]: {
    sentryDescription: "A work order concession is already open for this work order task",
  },
  [DbErrorType.SOW_CHANGE_PAYLOAD_INVALID]: {
    sentryDescription: "A SOW change payload does not parse as the request schema for its type",
  },
  [DbErrorType.SOW_CHANGE_LOCK_OUTSIDE_TRANSACTION]: {
    sentryDescription: "A SOW change row lock ran on a bare client, where it releases at autocommit and guards nothing",
  },
  [DbErrorType.LOAN_PLAN_NOT_FOUND]: {
    sentryDescription: "A loan rate sheet publish targeted a lender's plan that is missing",
  },
};
